import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  Plus,
  QrCode,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { sendInvitationEmail } from "@/lib/apiClient";
import { formatDoctorName } from "@/lib/doctorName";
import LogoUploader from "@/components/settings/LogoUploader";
import QRCropper from "@/components/settings/QRCropper";
import PatientPaymentLinkCard from "@/components/payments/PatientPaymentLinkCard";
import { useTutorial } from "@/tutorials/TutorialProvider";
import { DYNAMIC_CHARTING_MODULE_SEQUENCE } from "@/tutorials/tutorialRegistry";

const DAYS = [
  ["monday", "Monday"],
  ["tuesday", "Tuesday"],
  ["wednesday", "Wednesday"],
  ["thursday", "Thursday"],
  ["friday", "Friday"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
];
const DAY_LABELS = Object.fromEntries(DAYS);
const PAYMENT_METHODS = [
  { key: "gcash", label: "GCash" },
  { key: "maya", label: "Maya" },
  { key: "bank_transfer", label: "Bank Transfer" },
];
const CLINIC_TIME_ZONE = "Asia/Manila";
const ONBOARDING_TOUR_STEPS = [
  { key: "clinic", label: "Clinic created", status: "done" },
  { key: "tour", label: "Dynamic Charting + Checkout" },
  { key: "first_patient", label: "Create your first patient" },
  { key: "staff", label: "Invite your staff" },
];

function defaultHours() {
  return Object.fromEntries(DAYS.map(([key]) => [
    key,
    key === "sunday"
      ? { open: "08:00", close: "18:00", closed: true }
      : { open: "08:00", close: "18:00", closed: false },
  ]));
}

function timeOptions() {
  const out = [];
  for (let h = 6; h <= 22; h += 1) {
    for (const m of [0, 30]) {
      if (h === 22 && m === 30) continue;
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
}
const TIMES = timeOptions();

function minutesFromTime(value) {
  const [hours, minutes] = String(value || "00:00").split(":").map(Number);
  return hours * 60 + minutes;
}

function timeFromMinutes(value) {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function timeOptionsForClinicDay(hours, day) {
  const dayHours = hours?.[day] || defaultHours()[day];
  if (!dayHours || dayHours.closed) return [];
  const start = minutesFromTime(dayHours.open || "08:00");
  const end = minutesFromTime(dayHours.close || "18:00");
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  const out = [];
  for (let value = start; value < end; value += 15) {
    out.push(timeFromMinutes(value));
  }
  return out;
}

function localId(prefix = "row") {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;
}

function money(cents) {
  return `PHP ${Math.round(Number(cents || 0) / 100).toLocaleString("en-PH")}`;
}

function pesosToCentavos(value) {
  const n = Number(String(value || "0").replace(/,/g, ""));
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n * 100));
}

function centavosToPesos(cents) {
  return String(Math.round(Number(cents || 0) / 100));
}

function appointmentWindow(row, durationMinutes) {
  const start = minutesFromTime(row?.time);
  const duration = Math.max(15, Number(durationMinutes || 30));
  return { start, end: start + duration };
}

function windowsOverlap(a, b) {
  return Number.isFinite(a.start)
    && Number.isFinite(a.end)
    && Number.isFinite(b.start)
    && Number.isFinite(b.end)
    && a.start < b.end
    && b.start < a.end;
}

export function weekDate(dayIndex) {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayIndex);
  return localDateKey(date);
}

function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function clinicDateKeyFromInstant(value) {
  if (!value) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CLINIC_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function clinicTimeFromInstant(value) {
  if (!value) return "09:00";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CLINIC_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(value));
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("hour")}:${get("minute")}`;
}

function scheduleRangeBounds(scheduleDays) {
  const first = scheduleDays[0]?.key || weekDate(0);
  const last = scheduleDays[scheduleDays.length - 1]?.key || weekDate(6);
  const from = new Date(`${first}T00:00:00+08:00`);
  const to = new Date(`${last}T00:00:00+08:00`);
  to.setUTCDate(to.getUTCDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

function appointmentIsCancelled(appointment) {
  const status = String(appointment?.status || "").toLowerCase();
  return status.startsWith("cancel");
}

function normalizeProcedureName(value) {
  return String(value || "").trim().toLowerCase();
}

function procedureLabelForAppointment(appointment) {
  return String(
    appointment?.procedures
      || appointment?.procedure_name
      || appointment?.procedure
      || appointment?.notes
      || ""
  ).trim();
}

function appointmentToSavedRow(appointment, procedures) {
  const label = procedureLabelForAppointment(appointment);
  const procedure = procedures.find((item) => normalizeProcedureName(item.name) === normalizeProcedureName(label));
  const amountCentavos = appointment?.amount_centavos
    ?? appointment?.expected_amount_centavos
    ?? appointment?.performed_price_centavos
    ?? procedure?.default_fee_centavos
    ?? 0;
  return {
    id: `saved-${appointment.id || localId("appointment")}`,
    patient_name: appointment?.patient_name || "Scheduled patient",
    procedure_id: procedure?.id || (label ? "custom" : ""),
    custom_name: procedure ? "" : label,
    time: clinicTimeFromInstant(appointment?.scheduled_start),
    doctor_id: appointment?.doctor_id || "",
    chair_id: appointment?.chair_id || "",
    amount_pesos: amountCentavos ? centavosToPesos(amountCentavos) : "",
    saved: true,
    appointment_id: appointment?.id || null,
    saved_at: appointment?.created_at || appointment?.scheduled_start || null,
  };
}

function groupSavedAppointmentsByDay(appointments, procedures, scheduleDays) {
  const scheduleKeys = new Set(scheduleDays.map((day) => day.key));
  const grouped = Object.fromEntries(scheduleDays.map((day) => [day.key, []]));
  for (const appointment of appointments || []) {
    if (appointmentIsCancelled(appointment)) continue;
    const day = clinicDateKeyFromInstant(appointment?.scheduled_start);
    if (!scheduleKeys.has(day)) continue;
    grouped[day].push(appointmentToSavedRow(appointment, procedures));
  }
  for (const rows of Object.values(grouped)) {
    rows.sort((a, b) => minutesFromTime(a.time) - minutesFromTime(b.time));
  }
  return grouped;
}

function dayKeyForDate(date) {
  return DAYS[date.getDay() === 0 ? 6 : date.getDay() - 1][0];
}

function rollingScheduleDays() {
  const now = new Date();
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + index);
    const dayKey = dayKeyForDate(date);
    return {
      key: localDateKey(date),
      dayKey,
      weekday: DAY_LABELS[dayKey],
      monthDay: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    };
  });
}

export default function ClinicOnboardingSetup({ state, clinicName, onRefresh }) {
  const { startTutorial, startTutorialSequence, markTutorialCompleted } = useTutorial();
  const [modal, setModal] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [skippingTour, setSkippingTour] = useState(false);
  const items = useMemo(() => (Array.isArray(state?.items) ? state.items : []), [state?.items]);
  const stateItemCompleted = useCallback((keys) => (
    items.some((item) => keys.includes(item?.key) && item?.completed === true)
  ), [items]);
  const tourCompleted = stateItemCompleted(["tour", "tutorials", "take_tour", "dynamic_charting"]);
  const firstPatientCompleted = stateItemCompleted(["first_patient", "patient", "patients", "create_patient"]);
  const staffInviteCompleted = stateItemCompleted(["staff", "staff_invite", "team", "invite_staff"]);
  const tourSteps = ONBOARDING_TOUR_STEPS.map((step) => {
    if (step.key === "clinic") return { ...step, status: "done" };
    if (step.key === "tour") return { ...step, status: tourCompleted ? "done" : "active" };
    if (step.key === "first_patient") return { ...step, status: firstPatientCompleted ? "done" : tourCompleted ? "active" : "pending" };
    if (step.key === "staff") return { ...step, status: staffInviteCompleted ? "done" : firstPatientCompleted ? "active" : "pending" };
    return { ...step, status: "pending" };
  });
  const activeTourStep = tourSteps.find((step) => step.status === "active");
  const allTourStepsComplete = tourSteps.every((step) => step.status === "done");
  const primaryActionLabel = activeTourStep?.key === "first_patient"
    ? "Create your first patient →"
    : activeTourStep?.key === "staff"
      ? "Invite your staff →"
      : "Start tour →";

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const refreshAfterPatientCreated = () => onRefresh?.();
    window.addEventListener("tutorial:first-patient-created", refreshAfterPatientCreated);
    return () => window.removeEventListener("tutorial:first-patient-created", refreshAfterPatientCreated);
  }, [onRefresh]);

  function beginWalkthrough() {
    setIntroOpen(false);
    startTutorialSequence(DYNAMIC_CHARTING_MODULE_SEQUENCE);
  }

  function handleChecklistAction(step) {
    if (step.key === "first_patient" && step.status === "active") {
      startTutorial("patient-registration");
    }
  }

  function handlePrimaryAction() {
    if (activeTourStep?.key === "first_patient") {
      startTutorial("patient-registration");
      return;
    }
    if (activeTourStep?.key === "staff") {
      setModal("team");
      return;
    }
    setIntroOpen(true);
  }

  const skipTour = useCallback(async () => {
    if (skippingTour) return;
    setSkippingTour(true);
    const results = await Promise.all(
      DYNAMIC_CHARTING_MODULE_SEQUENCE.map((tutorialId) => markTutorialCompleted(tutorialId))
    );
    setSkippingTour(false);
    if (results.some((result) => result !== true)) {
      toast.error("Could not skip the tour yet.");
      return;
    }
    setIntroOpen(false);
    onRefresh?.();
  }, [markTutorialCompleted, onRefresh, skippingTour]);

  if (!state?.visible || allTourStepsComplete) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 via-slate-50 to-teal-50 shadow-sm" data-testid="onboarding-setup-card">
      <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="p-5 sm:p-6">
          <h2 className="text-2xl font-semibold tracking-normal text-slate-900">See how Dental OS works</h2>
          <div className="mt-6">
            <button
              type="button"
              onClick={handlePrimaryAction}
              data-testid="onboarding-start-tour"
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300/60"
            >
              {primaryActionLabel}
            </button>
          </div>
        </div>

        <ol className="space-y-5 border-sky-100 bg-slate-100/70 p-5 sm:p-6 lg:border-l" aria-label="Onboarding progress">
          {tourSteps.map((step) => {
            const isAction = step.key === "first_patient" && step.status === "active";
            return (
              <li key={step.key} className="flex items-center gap-3" data-testid={`onboarding-step-${step.key}`} data-status={step.status}>
                <span
                  className={[
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white/75",
                    step.status === "done" ? "border-teal-500 text-teal-600" : "",
                    step.status === "active" ? "border-blue-500 text-blue-600" : "",
                    step.status === "pending" ? "border-dashed border-slate-300 text-slate-400" : "",
                  ].filter(Boolean).join(" ")}
                >
                  {step.status === "done" ? <CheckCircle2 className="h-4 w-4" /> : <span className="h-2.5 w-2.5 rounded-sm border border-current" />}
                </span>
                {isAction ? (
                  <button
                    type="button"
                    onClick={() => handleChecklistAction(step)}
                    data-testid="onboarding-step-action-first_patient"
                    className="text-left text-sm font-semibold text-slate-800 underline decoration-blue-300 underline-offset-4 transition-colors hover:text-blue-700"
                  >
                    {step.label}
                  </button>
                ) : (
                  <span className={step.status === "pending" ? "text-sm font-semibold text-slate-400" : "text-sm font-semibold text-slate-800"}>
                    {step.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {modal === "hours" ? <HoursModal onClose={() => setModal(null)} onSaved={onRefresh} /> : null}
      {modal === "logo" ? <LogoModal clinicName={clinicName} onClose={() => setModal(null)} onSaved={onRefresh} /> : null}
      {modal === "team" ? <TeamModal onClose={() => setModal(null)} onSaved={onRefresh} /> : null}
      {modal === "payments" ? <PaymentsModal clinicName={clinicName} onClose={() => setModal(null)} onSaved={onRefresh} /> : null}
      {modal === "week" ? <WeekModal onClose={() => setModal(null)} onSaved={onRefresh} /> : null}
      {introOpen ? (
        <TourIntroOverlay
          onBegin={beginWalkthrough}
          onSkip={skipTour}
          skipping={skippingTour}
        />
      ) : null}
    </section>
  );
}

function TourIntroOverlay({ onBegin, onSkip, skipping }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4" role="dialog" aria-modal="true" aria-labelledby="onboarding-tour-intro-title" data-testid="onboarding-tour-intro">
      <div className="w-full max-w-lg rounded-2xl border border-sky-100 bg-slate-50 p-6 shadow-2xl">
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">5 steps · about 2 minutes</p>
        <h2 id="onboarding-tour-intro-title" className="mt-3 text-2xl font-semibold text-slate-900">Dynamic Charting</h2>
        <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
          <p>The chart isn’t a record — it’s a live system. Every finding flows into scheduling and treatment planning automatically.</p>
          <p>This is a read-only walkthrough. We’ll point out where things live — we won’t click or save anything for you.</p>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onBegin}
            data-testid="onboarding-begin-walkthrough"
            className="inline-flex min-h-11 items-center justify-center rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300/50"
          >
            Begin walkthrough →
          </button>
          <button
            type="button"
            onClick={onSkip}
            disabled={skipping}
            data-testid="onboarding-tour-skip"
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-300/60 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {skipping ? "Skipping..." : "Skip"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className={`bg-white rounded-2xl shadow-xl border border-slate-200 max-h-[90vh] overflow-hidden flex flex-col ${wide ? "w-full max-w-5xl" : "w-full max-w-2xl"}`}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function HoursModal({ onClose, onSaved }) {
  const [hours, setHours] = useState(defaultHours());
  const [doctors, setDoctors] = useState([]);
  const [availability, setAvailability] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [settingsRes, doctorsRes, availabilityRes] = await Promise.all([
        supabase.rpc("get_clinic_settings"),
        supabase.from("doctors").select("id, display_name").eq("archived", false),
        supabase.from("doctor_availability").select("*"),
      ]);
      if (!alive) return;
      const loadedHours = settingsRes.data?.hours && Object.keys(settingsRes.data.hours).length ? settingsRes.data.hours : defaultHours();
      const doctorRows = doctorsRes.data || [];
      setHours(loadedHours);
      setDoctors(doctorRows);
      const next = {};
      for (const doctor of doctorRows) {
        next[doctor.id] = {};
        for (const [day] of DAYS) {
          const existing = (availabilityRes.data || []).find((r) => r.doctor_id === doctor.id && r.day_of_week === day);
          const clinicDay = loadedHours[day] || defaultHours()[day];
          next[doctor.id][day] = existing
            ? { start_time: existing.start_time?.slice(0, 5) || clinicDay.open, end_time: existing.end_time?.slice(0, 5) || clinicDay.close, is_available: existing.is_available }
            : { start_time: clinicDay.open, end_time: clinicDay.close, is_available: !clinicDay.closed };
        }
      }
      setAvailability(next);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  function updateHour(day, patch) {
    setHours((h) => ({ ...h, [day]: { ...h[day], ...patch } }));
  }

  function updateAvail(doctorId, day, patch) {
    setAvailability((a) => ({
      ...a,
      [doctorId]: { ...a[doctorId], [day]: { ...a[doctorId]?.[day], ...patch } },
    }));
  }

  async function save() {
    setBusy(true);
    const rows = [];
    for (const doctor of doctors) {
      for (const [day] of DAYS) {
        const v = availability[doctor.id]?.[day];
        if (!v) continue;
        rows.push({
          doctor_id: doctor.id,
          day_of_week: day,
          start_time: v.start_time,
          end_time: v.end_time,
          is_available: !!v.is_available,
        });
      }
    }
    const { data, error } = await supabase.rpc("save_onboarding_hours_and_availability", {
      p_hours: hours,
      p_availability: rows,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Could not save hours.");
      return;
    }
    toast.success("Hours saved.");
    onSaved?.(data);
    onClose();
  }

  return (
    <Modal title="Clinic hours + doctor availability" onClose={onClose} wide>
      <div className="overflow-y-auto p-5 space-y-6">
        {loading ? <Loader /> : (
          <>
            <section>
              <h3 className="text-sm font-bold text-slate-900">Clinic hours</h3>
              <div className="mt-3 space-y-2">
                {DAYS.map(([day, label]) => {
                  const row = hours[day] || defaultHours()[day];
                  return (
                    <div key={day} className="grid grid-cols-[8rem_1fr] sm:grid-cols-[9rem_1fr_1fr_8rem] gap-2 items-center rounded-lg border border-slate-100 px-3 py-2">
                      <span className="text-sm font-semibold text-slate-700">{label}</span>
                      {!row.closed ? (
                        <>
                          <TimeSelect value={row.open} onChange={(open) => updateHour(day, { open })} />
                          <TimeSelect value={row.close} onChange={(close) => updateHour(day, { close })} />
                        </>
                      ) : <span className="sm:col-span-2 text-sm text-slate-400">Closed</span>}
                      <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={!row.closed} onChange={(e) => updateHour(day, { closed: !e.target.checked })} />
                        Open
                      </label>
                    </div>
                  );
                })}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold text-slate-900">Doctor availability</h3>
              <div className="mt-3 space-y-4">
                {doctors.map((doctor) => (
                  <div key={doctor.id} className="rounded-xl border border-slate-100 p-3">
                    <p className="text-sm font-semibold text-slate-900">{doctor.display_name || "Doctor"}</p>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {DAYS.map(([day, label]) => {
                        const row = availability[doctor.id]?.[day] || { start_time: "08:00", end_time: "18:00", is_available: day !== "sunday" };
                        return (
                          <div key={day} className="grid grid-cols-[5.5rem_1fr_1fr_4rem] gap-2 items-center">
                            <span className="text-xs font-medium text-slate-600">{label.slice(0, 3)}</span>
                            <TimeSelect value={row.start_time} onChange={(start_time) => updateAvail(doctor.id, day, { start_time })} disabled={!row.is_available} />
                            <TimeSelect value={row.end_time} onChange={(end_time) => updateAvail(doctor.id, day, { end_time })} disabled={!row.is_available} />
                            <input type="checkbox" checked={row.is_available} onChange={(e) => updateAvail(doctor.id, day, { is_available: e.target.checked })} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
      <Footer busy={busy} onCancel={onClose} onSave={save} />
    </Modal>
  );
}

function LogoModal({ clinicName, onClose, onSaved }) {
  const { activeClinic } = useAuth();
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [skipping, setSkipping] = useState(false);
  useEffect(() => {
    supabase.rpc("get_clinic_settings").then(({ data }) => {
      setLogoUrl(data?.logo_url || "");
      setLoading(false);
    });
  }, []);

  async function skipLogoStep() {
    setSkipping(true);
    const { data, error } = await supabase.rpc("skip_onboarding_logo_step");
    setSkipping(false);
    if (error) {
      toast.error(error.message ?? "Could not skip logo setup.");
      return;
    }
    toast.success("Logo step skipped.");
    onSaved?.(data);
    onClose();
  }

  return (
    <Modal title="Upload your logo" onClose={onClose}>
      <div className="p-5">
        {loading ? <Loader /> : (
          <LogoUploader
            clinicId={activeClinic?.clinic_id || activeClinic?.id}
            clinicName={clinicName}
            logoUrl={logoUrl}
            showNamePreview
            testIdPrefix="onboarding-logo"
            onChange={(url) => {
              setLogoUrl(url);
              onSaved?.();
            }}
          />
        )}
      </div>
      <Footer
        busy={skipping}
        onCancel={onClose}
        onSave={onClose}
        saveLabel="Done"
        leftAction={(
          <button
            type="button"
            onClick={skipLogoStep}
            disabled={skipping}
            data-testid="onboarding-logo-skip"
            className="text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50"
          >
            {skipping ? "Skipping..." : "Skip logo"}
          </button>
        )}
      />
    </Modal>
  );
}

function TeamModal({ onClose, onSaved }) {
  const { user } = useAuth();
  const [doctorRows, setDoctorRows] = useState([emptyTeamRow()]);
  const [frontRows, setFrontRows] = useState([emptyTeamRow()]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      const [membersRes, invitationsRes] = await Promise.all([
        supabase.rpc("list_clinic_members"),
        supabase.rpc("list_pending_invitations"),
      ]);
      if (!alive) return;

      const members = Array.isArray(membersRes.data) ? membersRes.data : [];
      const pending = Array.isArray(invitationsRes.data)
        ? invitationsRes.data.filter((inv) => inv.status === "pending")
        : [];
      const pendingInviteFor = (member) => {
        if (member.email) return null;
        const name = (member.doctor_display_name || member.full_name || "").trim();
        return pending.find((inv) => {
          const inviteName = (inv.invitee_display_name || inv.invitee_full_name || "").trim();
          return inv.role === member.role && inviteName && inviteName === name;
        });
      };
      const pendingEmailFor = (member) => {
        if (member.email) return member.email;
        return pendingInviteFor(member)?.email || "";
      };
      const rowIdFor = (member) => member.user_id || member.doctor_id || member.staff_id || localId("team");
      const toRow = (member) => ({
        id: rowIdFor(member),
        name: (member.doctor_display_name || member.full_name || "").trim(),
        email: pendingEmailFor(member),
        doctor_id: member.doctor_id || null,
        staff_id: member.staff_id || null,
        invitation_id: pendingInviteFor(member)?.id || null,
        roster_only: !!member.roster_only,
        persisted: true,
      });
      const doctors = members
        .filter((member) => member.role === "doctor" && !member.is_you)
        .map(toRow)
        .filter((row) => row.name || row.email);
      const staff = members
        .filter((member) => ["receptionist", "assistant"].includes(member.role) && !member.is_you)
        .map((member) => ({ ...toRow(member), role: member.role || "receptionist" }))
        .filter((row) => row.name || row.email);

      const hasMemberForInvite = (inv) => {
        const inviteName = (inv.invitee_display_name || inv.invitee_full_name || "").trim();
        return members.some((member) => {
          const memberName = (member.doctor_display_name || member.full_name || "").trim();
          return member.role === inv.role && inviteName && memberName === inviteName;
        });
      };
      for (const inv of pending) {
        if (hasMemberForInvite(inv)) continue;
        const row = {
          id: inv.id || localId("team"),
          name: (inv.invitee_display_name || inv.invitee_full_name || "").trim(),
          email: inv.email || "",
          invitation_id: inv.id || null,
          persisted: true,
        };
        if (!row.name && !row.email) continue;
        if (inv.role === "doctor") doctors.push(row);
        if (["receptionist", "assistant"].includes(inv.role)) staff.push({ ...row, role: inv.role });
      }

      setDoctorRows(doctors.length ? doctors : [emptyTeamRow()]);
      setFrontRows(staff.length ? staff : [emptyTeamRow()]);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  async function removeTeamRow(kind, row) {
    if (!row.persisted) {
      const setter = kind === "doctor" ? setDoctorRows : setFrontRows;
      setter((rows) => rows.filter((item) => item.id !== row.id));
      return;
    }

    if (!row.doctor_id && !row.staff_id) return;
    if (!window.confirm(`Remove ${row.name || "this person"} from the roster?`)) return;
    const { error: archiveError } = await supabase.rpc("archive_roster_member", {
      p_doctor_id: row.doctor_id || null,
      p_staff_id: row.staff_id || null,
    });
    if (archiveError) {
      toast.error(archiveError.message ?? "Could not remove roster row.");
      return;
    }
    if (row.invitation_id) {
      const { error: revokeError } = await supabase.rpc("revoke_invitation", {
        p_invitation_id: row.invitation_id,
        p_reason: "Roster row removed during onboarding.",
      });
      if (revokeError) {
        toast.warning(revokeError.message ?? "Roster removed, but invitation could not be revoked.");
      }
    }

    const setter = kind === "doctor" ? setDoctorRows : setFrontRows;
    setter((rows) => rows.filter((item) => item.id !== row.id));
    toast.success("Roster entry removed.");
    onSaved?.();
  }

  function normalizeRows(rows, role) {
    const out = [];
    for (const row of rows) {
      const name = row.name.trim();
      const email = row.email.trim();
      if (!name && !email) continue;
      if (!name) throw new Error("Each team row needs a name");
      out.push({ name, email, role });
    }
    return out;
  }

  async function save() {
    setBusy(true);
    setError("");
    try {
      const doctors = normalizeRows(doctorRows.filter((row) => !row.persisted), "doctor").map(({ name, email }) => ({ name, email }));
      const staff = normalizeRows(frontRows.filter((row) => !row.persisted), "receptionist");
      if (doctors.length === 0 && staff.length === 0) {
        toast.success("Team roster saved.");
        onSaved?.();
        onClose();
        return;
      }
      const { data, error: rpcError } = await supabase.rpc("save_onboarding_team_roster", {
        p_doctors: doctors,
        p_staff: staff,
      });
      if (rpcError) throw new Error(rpcError.message);

      const invitations = Array.isArray(data?.invitations) ? data.invitations : [];
      for (const inv of invitations) {
        await sendInvitationEmail({
          to_email: inv.email,
          clinic_name: inv.clinic_name,
          clinic_slug: inv.clinic_slug,
          role: inv.role,
          code: inv.code,
          invited_by: inv.invited_by || user?.user_metadata?.full_name || user?.email || "The clinic admin",
        });
      }

      toast.success(invitations.length ? "Team roster and invitations saved." : "Team roster saved.");
      onSaved?.(data);
      onClose();
    } catch (e) {
      const message = e.message ?? "Could not save team roster.";
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Fill in your team" onClose={onClose}>
      <div className="overflow-y-auto p-5 space-y-5">
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
          <p className="text-sm font-semibold text-slate-900">{user?.user_metadata?.full_name || user?.email || "Owner"}</p>
          <p className="text-xs text-slate-500">Owner</p>
        </div>
        {loading ? <Loader /> : (
          <>
            <TeamSection title="Doctors" rows={doctorRows} setRows={setDoctorRows} onRemove={(row) => removeTeamRow("doctor", row)} />
            <TeamSection title="Front desk" rows={frontRows} setRows={setFrontRows} onRemove={(row) => removeTeamRow("staff", row)} />
          </>
        )}
        {error ? <p className="text-sm text-rose-600" data-testid="onboarding-team-error">{error}</p> : null}
      </div>
      <Footer busy={busy} onCancel={onClose} onSave={save} />
    </Modal>
  );
}

function PaymentsModal({ clinicName, onClose, onSaved }) {
  const { activeClinic } = useAuth();
  const fileRef = useRef(null);
  const [methods, setMethods] = useState(new Set());
  const [qrs, setQrs] = useState([]);
  const [bank, setBank] = useState({ account_name: "", bank_name: "", account_number: "" });
  const [uploadMethod, setUploadMethod] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    supabase.rpc("get_clinic_settings").then(({ data }) => {
      setMethods(paymentMethodsFromSettings(data));
      setQrs(Array.isArray(data?.payment_qr_codes) ? data.payment_qr_codes : []);
      setBank({
        account_name: data?.extras?.bank_transfer?.account_name || "",
        bank_name: data?.extras?.bank_transfer?.bank_name || "",
        account_number: data?.extras?.bank_transfer?.account_number || "",
      });
    });
  }, []);

  function toggle(method) {
    setMethods((prev) => {
      const next = new Set(prev);
      if (next.has(method)) {
        next.delete(method);
      } else {
        next.add(method);
      }
      return next;
    });
  }

  function pickQr(method) {
    setUploadMethod(method);
    fileRef.current?.click();
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setPendingFile(file);
  }

  async function handleQrCropped(blob) {
    const method = uploadMethod;
    setPendingFile(null);
    if (!method || !activeClinic?.clinic_id) return;
    setUploading(true);
    const path = `${activeClinic.clinic_id}/${crypto.randomUUID()}.png`;
    const { error } = await supabase.storage.from("payment-qrs").upload(path, blob, { contentType: "image/png", upsert: false });
    if (error) {
      setUploading(false);
      toast.error(error.message ?? "QR upload failed.");
      return;
    }
    const { data } = supabase.storage.from("payment-qrs").getPublicUrl(path);
    const row = { id: crypto.randomUUID(), label: methodLabel(method), method, image_url: data?.publicUrl, archived: false };
    setQrs((prev) => [...prev.filter((qr) => qr.method !== method), row]);
    setMethods((prev) => new Set([...prev, method]));
    setUploading(false);
  }

  async function save() {
    setBusy(true);
    const { data, error } = await supabase.rpc("save_onboarding_payment_options", {
      p_input: {
        digital_payment_methods: [...methods],
        payment_qr_codes: qrs,
        bank_transfer: bank,
      },
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Could not save payment options.");
      return;
    }
    toast.success("Payment options saved.");
    onSaved?.(data);
    onClose();
  }

  const activeQr = qrs.find((qr) => methods.has(qr.method) && !qr.archived)
    || qrs.find((qr) => ["gcash", "maya", "bank_transfer"].includes(qr.method) && !qr.archived)
    || qrs.find((qr) => !qr.archived)
    || null;
  return (
    <Modal title="Set up payment options" onClose={onClose} wide>
      <div className="overflow-y-auto p-5 grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-5">
        <div className="space-y-3">
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          {PAYMENT_METHODS.map((m) => (
            <div key={m.key} className="rounded-xl border border-slate-100 p-3">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={methods.has(m.key)} onChange={() => toggle(m.key)} />
                <span className="text-sm font-semibold text-slate-900">{m.label}</span>
              </label>
              {methods.has(m.key) && ["gcash", "maya"].includes(m.key) ? (
                <div className="mt-3 flex items-center gap-3">
                  {qrs.find((qr) => qr.method === m.key && !qr.archived)?.image_url ? (
                    <img src={qrs.find((qr) => qr.method === m.key && !qr.archived)?.image_url} alt="" className="h-16 w-16 rounded border object-contain" />
                  ) : <QrCode className="h-8 w-8 text-slate-300" />}
                  <button type="button" onClick={() => pickQr(m.key)} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold">
                    <Upload className="w-3.5 h-3.5" /> Upload QR
                  </button>
                </div>
              ) : null}
              {methods.has(m.key) && m.key === "bank_transfer" ? (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <SmallInput value={bank.account_name} onChange={(account_name) => setBank((b) => ({ ...b, account_name }))} placeholder="Account name" />
                  <SmallInput value={bank.bank_name} onChange={(bank_name) => setBank((b) => ({ ...b, bank_name }))} placeholder="Bank name" />
                  <SmallInput value={bank.account_number} onChange={(account_number) => setBank((b) => ({ ...b, account_number }))} placeholder="Account number" />
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <CheckoutQrPreview activeQr={activeQr} clinicName={clinicName} />
      </div>
      <Footer busy={busy} onCancel={onClose} onSave={save} />
      {pendingFile ? (
        <QRCropper file={pendingFile} onCancel={() => setPendingFile(null)} onCropped={handleQrCropped} />
      ) : null}
    </Modal>
  );
}

function CheckoutQrPreview({ activeQr, clinicName }) {
  const previewLink = {
    status: "pending",
    clinic_name: clinicName || "Your clinic",
    amount_centavos: 150000,
    qr_image_url: activeQr?.image_url || null,
    qr_label: activeQr?.label || null,
  };
  const noop = () => {};
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4 border border-slate-100" data-testid="onboarding-checkout-payment-preview">
      <p className="text-sm font-semibold text-slate-800">Patient payment link preview</p>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-4 flex justify-center">
        <PatientPaymentLinkCard
          link={previewLink}
          onDownloadQR={noop}
          onPickFile={noop}
          onFileChosen={noop}
        />
      </div>
    </div>
  );
}

function WeekModal({ onClose, onSaved }) {
  const scheduleDays = useMemo(() => rollingScheduleDays(), []);
  const [activeDay, setActiveDay] = useState(() => scheduleDays[0]?.key || weekDate(0));
  const [procedures, setProcedures] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [chairs, setChairs] = useState([]);
  const [settings, setSettings] = useState({});
  const [rowsByDay, setRowsByDay] = useState(() => Object.fromEntries(scheduleDays.map((day) => [day.key, [emptyAppointmentRow()]])));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const clinicHours = settings.hours || defaultHours();
  const activeScheduleDay = scheduleDays.find((day) => day.key === activeDay) || scheduleDays[0];
  const activeTimeOptions = timeOptionsForClinicDay(clinicHours, activeScheduleDay?.dayKey || dayKeyForDate(new Date()));

  useEffect(() => {
    (async () => {
      const range = scheduleRangeBounds(scheduleDays);
      const [procs, docs, chairRows, clinicSettings, appointments] = await Promise.all([
        supabase.rpc("list_procedures_for_settings", { p_include_archived: false }),
        supabase.from("doctors").select("id, display_name").eq("archived", false),
        supabase.rpc("list_chairs_for_settings", { p_include_archived: false }),
        supabase.rpc("get_clinic_settings"),
        supabase.rpc("list_appointments", {
          p_from: range.from,
          p_to: range.to,
          p_doctor_id: null,
          p_chair_id: null,
        }),
      ]);
      const docRows = docs.data || [];
      const chairData = chairRows.data || [];
      const procedureRows = procs.data || [];
      const savedAppointmentsByDay = groupSavedAppointmentsByDay(appointments.data || [], procedureRows, scheduleDays);
      setProcedures(procedureRows);
      setDoctors(docRows);
      setChairs(chairData);
      setSettings(clinicSettings.data || {});
      setRowsByDay((prev) => {
        const next = { ...prev };
        for (const day of scheduleDays) {
          const dayTimeOptions = timeOptionsForClinicDay(clinicSettings.data?.hours || defaultHours(), day.dayKey);
          const loadedSavedRows = savedAppointmentsByDay[day.key] || [];
          const existingRows = next[day.key] || [];
          const draftRows = existingRows.filter((row) => !row.saved);
          const visibleDraftRows = draftRows.length ? draftRows : [emptyAppointmentRow()];
          const rows = [
            ...loadedSavedRows,
            ...visibleDraftRows,
          ];
          next[day.key] = rows.map((r) => ({
            ...r,
            time: r.saved || dayTimeOptions.includes(r.time) ? r.time : (dayTimeOptions[0] || r.time),
            doctor_id: r.doctor_id || docRows[0]?.id || "",
            chair_id: r.chair_id || chairData[0]?.id || "",
          }));
        }
        return next;
      });
    })();
  }, [scheduleDays]);

  function updateRow(day, id, patch) {
    setRowsByDay((prev) => ({
      ...prev,
      [day]: prev[day].map((r) => r.id === id ? { ...r, ...patch } : r),
    }));
  }

  function addRow(day) {
    const scheduleDay = scheduleDays.find((item) => item.key === day);
    const dayTimeOptions = timeOptionsForClinicDay(clinicHours, scheduleDay?.dayKey || dayKeyForDate(new Date()));
    setRowsByDay((prev) => ({
      ...prev,
      [day]: [...prev[day], emptyAppointmentRow(doctors[0]?.id, chairs[0]?.id, dayTimeOptions[0] || "09:00")],
    }));
  }

  function removeRow(day, id) {
    setRowsByDay((prev) => ({
      ...prev,
      [day]: prev[day].filter((r) => r.id !== id),
    }));
  }

  function chooseProcedure(day, row, procedureId) {
    const proc = procedures.find((p) => p.id === procedureId);
    updateRow(day, row.id, {
      procedure_id: procedureId,
      custom_name: "",
      amount_pesos: proc ? centavosToPesos(proc.default_fee_centavos) : row.amount_pesos,
    });
  }

  function rowHasAppointmentIntent(row) {
    return Boolean(row?.patient_name?.trim() || row?.procedure_id || row?.custom_name?.trim());
  }

  function findDraftOverlapErrors() {
    const nextErrors = {};
    const duration = Number(settings.default_duration_minutes || 30);
    const chairName = (id) => chairs.find((chair) => chair.id === id)?.name || "this chair";
    const doctorName = (id) => formatDoctorName(doctors.find((doctor) => doctor.id === id), "this doctor");

    for (const rows of Object.values(rowsByDay)) {
      const seen = [];
      for (const row of rows) {
        if (!rowHasAppointmentIntent(row)) continue;
        const currentWindow = appointmentWindow(row, duration);
        const conflict = seen.find((other) => {
          const otherWindow = appointmentWindow(other, duration);
          return windowsOverlap(currentWindow, otherWindow)
            && (
              (row.chair_id && other.chair_id && row.chair_id === other.chair_id)
              || (row.doctor_id && other.doctor_id && row.doctor_id === other.doctor_id)
            );
        });

        if (conflict && !row.saved) {
          if (row.chair_id && conflict.chair_id && row.chair_id === conflict.chair_id) {
            nextErrors[row.id] = `This overlaps another appointment for ${chairName(row.chair_id)}.`;
          } else {
            nextErrors[row.id] = `This overlaps another appointment for ${doctorName(row.doctor_id)}.`;
          }
        }
        seen.push(row);
      }
    }

    return nextErrors;
  }

  async function save() {
    setSaving(true);
    const nextErrors = findDraftOverlapErrors();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSaving(false);
      toast.error("Fix overlapping appointments before saving.");
      return;
    }
    const savedRows = {};
    let savedCount = 0;
    for (const [day, rows] of Object.entries(rowsByDay)) {
      for (const row of rows) {
        const key = row.id;
        if (row.saved) continue;
        if (!row.patient_name.trim() && !row.procedure_id && !row.custom_name.trim()) continue;
        try {
          if (!row.patient_name.trim()) throw new Error("Patient name is required.");
          if (!row.time) throw new Error("Time is required.");
          if (!row.doctor_id || !row.chair_id) throw new Error("Doctor and chair are required.");
          const amount = pesosToCentavos(row.amount_pesos);
          const selectedProcedure = procedures.find((p) => p.id === row.procedure_id);
          const custom = row.procedure_id === "custom";
          if (!selectedProcedure && !custom) throw new Error("Procedure is required.");
          if (custom && !row.custom_name.trim()) throw new Error("Custom procedure name is required.");

          const patient = await supabase.rpc("create_patient", {
            p_data: { full_name: row.patient_name.trim(), phone: null, quick_create: true, recall_opt_in: true },
          });
          if (patient.error) throw new Error(patient.error.message);

          let useCatalogProcedure = selectedProcedure;
          let scheduledItem = null;
          if (selectedProcedure && amount !== Number(selectedProcedure.default_fee_centavos || 0)) {
            const confirmUpdate = window.confirm(`Update ${selectedProcedure.name} default price to ${money(amount)}?`);
            if (confirmUpdate) {
              const upd = await supabase.rpc("update_procedure", {
                p_input: { id: selectedProcedure.id, default_fee_centavos: amount },
              });
              if (upd.error) throw new Error(upd.error.message);
            } else {
              useCatalogProcedure = null;
              scheduledItem = { procedure_id: selectedProcedure.id, performed_price_centavos: amount };
            }
          } else if (custom) {
            useCatalogProcedure = null;
            scheduledItem = { custom_name: row.custom_name.trim(), performed_price_centavos: amount };
          }

          const start = new Date(`${day}T${row.time}:00+08:00`);
          const end = new Date(start.getTime() + Number(settings.default_duration_minutes || 30) * 60000);
          const appt = await supabase.rpc("create_appointment", {
            p_input: {
              patient_id: patient.data?.patient_id || patient.data?.id,
              doctor_id: row.doctor_id,
              chair_id: row.chair_id,
              scheduled_start: start.toISOString(),
              scheduled_end: end.toISOString(),
              status: "Scheduled",
              walk_in: false,
              procedure_id: useCatalogProcedure?.id || null,
              notes: custom ? row.custom_name.trim() : null,
            },
          });
          if (appt.error) throw new Error(appt.error.message);
          savedRows[row.id] = {
            saved: true,
            appointment_id: appt.data?.id || null,
            saved_at: new Date().toISOString(),
          };
          savedCount += 1;

          if (scheduledItem) {
            const item = await supabase.rpc("create_appointment_scheduled_item", {
              p_input: { appointment_id: appt.data?.id, ...scheduledItem },
            });
            if (item.error) throw new Error(item.error.message);
          }

          if (custom && window.confirm(`Add ${row.custom_name.trim()} to your procedures list?`)) {
            const created = await supabase.rpc("create_procedure", {
              p_input: {
                name: row.custom_name.trim(),
                default_fee_centavos: amount,
                currency: settings.currency || "PHP",
                suggested_duration_minutes: Number(settings.default_duration_minutes || 30),
              },
            });
            if (created.error) toast.warning(created.error.message ?? "Appointment saved, but procedure was not added.");
          }
        } catch (e) {
          nextErrors[key] = e.message ?? "Could not save this row.";
        }
      }
    }
    setErrors(nextErrors);
    setSaving(false);
    if (savedCount > 0) {
      setRowsByDay((prev) => Object.fromEntries(
        Object.entries(prev).map(([day, rows]) => [
          day,
          rows.map((row) => savedRows[row.id] ? { ...row, ...savedRows[row.id] } : row),
        ])
      ));
    }
    if (Object.keys(nextErrors).length === 0) {
      toast.success(savedCount > 0 ? "Appointments saved." : "Nothing new to save.");
      onSaved?.();
    } else {
      toast.error("Some rows did not save.");
    }
  }

  return (
    <Modal title="Schedule your week" onClose={onClose} wide>
      <div className="overflow-y-auto p-5">
        <p className="mb-4 max-w-2xl text-sm text-slate-500">Get your existing appointments in so checkouts can start today.</p>
        <div className="flex gap-1 border-b border-slate-200">
          {scheduleDays.map((day) => (
            <button key={day.key} type="button" onClick={() => setActiveDay(day.key)} className={`px-3 py-2 text-xs font-semibold border-b-2 ${activeDay === day.key ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500"}`}>
              <span className="block">{day.weekday.slice(0, 3)}</span>
              <span className="mt-0.5 block text-[11px] font-medium">{day.monthDay}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-3">
          {rowsByDay[activeDay].map((row) => (
            <div key={row.id} className={`rounded-xl border p-3 space-y-2 ${row.saved ? "border-teal-100 bg-teal-50/40" : "border-slate-100"}`}>
              {row.saved ? (
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-semibold text-teal-800">Saved to schedule</span>
                  <span className="text-teal-700">{row.time}</span>
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,.7fr)_minmax(0,1fr)_minmax(0,.8fr)_minmax(0,.7fr)_2.25rem] gap-2 items-start">
                <SmallInput value={row.patient_name} onChange={(patient_name) => updateRow(activeDay, row.id, { patient_name })} placeholder="Patient name" disabled={row.saved} />
                <select value={row.procedure_id} onChange={(e) => chooseProcedure(activeDay, row, e.target.value)} disabled={row.saved} className="min-w-0 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500">
                  <option value="">Procedure</option>
                  {procedures.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  <option value="custom">Custom...</option>
                </select>
                <TimeSelect label="Time" value={row.time} options={activeTimeOptions} onChange={(time) => updateRow(activeDay, row.id, { time })} disabled={row.saved} />
                <select value={row.doctor_id} onChange={(e) => updateRow(activeDay, row.id, { doctor_id: e.target.value })} disabled={row.saved} className="min-w-0 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500">
                  {doctors.map((d) => <option key={d.id} value={d.id}>{formatDoctorName(d)}</option>)}
                </select>
                <select value={row.chair_id} onChange={(e) => updateRow(activeDay, row.id, { chair_id: e.target.value })} disabled={row.saved} className="min-w-0 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500">
                  {chairs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <SmallInput value={row.amount_pesos} onChange={(amount_pesos) => updateRow(activeDay, row.id, { amount_pesos })} placeholder="Amount" type="number" disabled={row.saved} />
                {row.saved ? (
                  <span className="h-9 w-9" aria-hidden="true" />
                ) : (
                  <button type="button" onClick={() => removeRow(activeDay, row.id)} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              {row.procedure_id === "custom" ? (
                <SmallInput value={row.custom_name} onChange={(custom_name) => updateRow(activeDay, row.id, { custom_name })} placeholder="Custom procedure name" disabled={row.saved} />
              ) : null}
              {errors[row.id] ? <p className="text-xs text-rose-600">{errors[row.id]}</p> : null}
            </div>
          ))}
          <button type="button" onClick={() => addRow(activeDay)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
            <Plus className="w-3.5 h-3.5" /> Add appointment
          </button>
        </div>
      </div>
      <Footer busy={saving} onCancel={onClose} onSave={save} />
    </Modal>
  );
}

function TeamSection({ title, rows, setRows, onRemove }) {
  return (
    <section>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <button type="button" onClick={() => setRows((r) => [...r, emptyTeamRow()])} className="text-xs font-semibold text-teal-700">Add more</button>
      </div>
      <div className="mt-2 space-y-2">
        {rows.map((row, index) => (
          <div
            key={row.id || index}
            className={`grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 rounded-lg ${row.persisted ? "bg-slate-50" : ""}`}
          >
            <SmallInput value={row.name} onChange={(name) => setRows((r) => r.map((x, i) => i === index ? { ...x, name } : x))} placeholder="Full name" disabled={row.persisted} />
            <SmallInput value={row.email} onChange={(email) => setRows((r) => r.map((x, i) => i === index ? { ...x, email } : x))} placeholder="Email optional" type="email" disabled={row.persisted} />
            <button
              type="button"
              data-testid={row.persisted && (row.doctor_id || row.staff_id) ? `onboarding-team-remove-${row.doctor_id || row.staff_id}` : undefined}
              onClick={() => onRemove?.(row)}
              disabled={row.persisted && !row.doctor_id && !row.staff_id}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
              title={row.persisted ? "Remove from roster and Settings" : "Remove row"}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function TimeSelect({ value, onChange, disabled = false, options = TIMES, label }) {
  const normalizedOptions = options.length ? options : [value || ""];
  return (
    <select aria-label={label} value={value || normalizedOptions[0] || "08:00"} onChange={(e) => onChange(e.target.value)} disabled={disabled || options.length === 0} className="min-w-0 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400">
      {options.length === 0 ? <option value={value || ""}>Closed</option> : null}
      {options.length ? normalizedOptions.map((t) => <option key={t} value={t}>{t}</option>) : null}
    </select>
  );
}

function SmallInput({ value, onChange, placeholder, type = "text", disabled = false }) {
  return (
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} className="min-w-0 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:opacity-100" />
  );
}

function Footer({ busy = false, onCancel, onSave, saveLabel = "Save", leftAction = null }) {
  return (
    <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-2 bg-white">
      <div>{leftAction}</div>
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
        <button type="button" onClick={onSave} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-teal-400">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {busy ? "Saving..." : saveLabel}
        </button>
      </div>
    </div>
  );
}

function Loader() {
  return <div className="p-8 text-center text-sm text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />Loading...</div>;
}

function methodLabel(key) {
  return PAYMENT_METHODS.find((m) => m.key === key)?.label || key;
}

function paymentMethodsFromSettings(data) {
  const selected = new Set(Array.isArray(data?.digital_payment_methods) ? data.digital_payment_methods : []);
  const qrs = Array.isArray(data?.payment_qr_codes) ? data.payment_qr_codes : [];
  for (const qr of qrs) {
    if (qr?.method && !qr.archived) selected.add(qr.method);
  }
  const bank = data?.extras?.bank_transfer || {};
  if (bank.account_name || bank.bank_name || bank.account_number) selected.add("bank_transfer");
  return selected;
}

function emptyTeamRow() {
  return { id: localId("team"), name: "", email: "" };
}

function emptyAppointmentRow(doctorId = "", chairId = "", time = "09:00") {
  return {
    id: localId("appointment"),
    patient_name: "",
    procedure_id: "",
    custom_name: "",
    time,
    doctor_id: doctorId,
    chair_id: chairId,
    amount_pesos: "",
  };
}
